import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Autocomplete,
  TextField,
  IconButton
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from '@mui/icons-material/Search';
import { fetchReport, handleError, formatDateForMySQL, formatCurrency, formatDate2, fetchProducts } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useReportDatePickerLocale } from '../../hooks/useReportDatePickerLocale';

const ProductsTraceabilityReport = () => {
  const { t } = useTranslation();
  const { datePickerFormat, dayjsPickerLocale } = useReportDatePickerLocale();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { setNavigation, resetNavigation, state } = useNavigationHelperStore();
  
  // Date filters
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  
  // Load products for autocomplete
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoadingProducts(true);
        const productsData = await fetchProducts();
        // Filter only inventory products
        const inventoryProducts = productsData.filter(product => product.has_stock);
        setProducts(inventoryProducts);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    
    loadProducts();
  }, []);

  // Load saved search parameters when returning to the page
  useEffect(() => {
    if (state) {
      const { startDate: savedStartDate, endDate: savedEndDate, page: savedPage, selectedProduct: savedProduct } = state;
      if (savedStartDate) setStartDate(dayjs(savedStartDate));
      if (savedEndDate) setEndDate(dayjs(savedEndDate));
      if (savedPage) setPage(savedPage);
      if (savedProduct) setSelectedProduct(savedProduct);
      handleSearch();
    }
  }, []);

  // Save search parameters when leaving this page
  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      page,
      data,
      selectedProduct
    });
  };

  const handleSearch = async () => {
    if (!selectedProduct) {
      snackbar.showSnackbar(t('reports.selectProductFirst'), "error");
      return;
    }

    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('PRODUCTS_TRACEABILITY', 1, {
        from: formattedStartDate,
        to: formattedEndDate,
        product_id: selectedProduct.id
      });
      
      setData(response);
      setPage(1);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePageChange = async (event, value) => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const response = await fetchReport('PRODUCTS_TRACEABILITY', value, {
        from: formattedStartDate,
        to: formattedEndDate,
        product_id: selectedProduct.id
      });
      
      setData(response);
      setPage(value);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMovementClick = (movement) => {
    // Save current state before navigating
    saveCurrentState();
    
    if (movement.movement_type === 'SELL') {
      navigate('/main/view-sell', { state: { id: movement.reference_id } });
    } else if (movement.movement_type === 'PURCHASE') {
      navigate('/main/view-purchase', { state: { id: movement.reference_id } });
    } else if (movement.movement_type === 'MOVEMENT') {
      navigate('/main/view-movement', { state: { id: movement.reference_id } });
    }
  };

  const getMovementTypeChip = (type) => {
    switch (type) {
      case 'IN':
        return <Chip label={t('reports.entryType')} color="success" size="small" />;
      case 'OUT':
        return <Chip label={t('reports.exitType')} color="error" size="small" />;
      case 'SELL':
        return <Chip label={t('reports.saleType')} color="primary" size="small" />;
      case 'PURCHASE':
        return <Chip label={t('reports.purchaseType')} color="secondary" size="small" />;
      default:
        return <Chip label={type || '-'} size="small" />;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={products}
              getOptionLabel={(option) => option ? option.name : ''}
              value={selectedProduct}
              onChange={(event, newValue) => {
                setSelectedProduct(newValue);
              }}
              loading={isLoadingProducts}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <div>
                    <div>{option.name}</div>
                    <div style={{ fontSize: '0.8em', color: 'gray' }}>
                      {option.sku || t('reports.noCode')}
                    </div>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('reports.selectProduct')}
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <DatePicker
                    label={t('common.from')}
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    format={datePickerFormat}
                    slotProps={{
                      textField: { fullWidth: true, variant: 'outlined' },
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={5}>
                  <DatePicker
                    label={t('common.to')}
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    format={datePickerFormat}
                    minDate={startDate}
                    slotProps={{
                      textField: { fullWidth: true, variant: 'outlined' },
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <IconButton 
                    onClick={handleSearch}
                    disabled={!selectedProduct || isLoading}
                    sx={{ 
                      p: 1, 
                      backgroundColor: 'primary.main', 
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'action.disabledBackground',
                      }
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Box>
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t('reports.traceabilityFor')} {selectedProduct?.name} ({data.total_results || data.content.length})
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                      <Typography variant="subtitle1">
                        {t('reports.entries')} {data.total_in || 0} {t('reports.units')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                      <Typography variant="subtitle1">
                        {t('reports.exits')} {data.total_out || 0} {t('reports.units')}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
              
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table sx={{ minWidth: 350 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.date')}</TableCell>
                      <TableCell>{t('common.type')}</TableCell>
                      <TableCell>{t('common.receipt')}</TableCell>
                      <TableCell>{t('common.quantity')}</TableCell>
                      <TableCell>{t('common.price')}</TableCell>
                      <TableCell>{t('reports.originDestination')}</TableCell>
                      <TableCell>{t('common.notes')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.content.map((movement) => (
                      <TableRow 
                        key={movement.id}
                        hover
                        onClick={() => handleMovementClick(movement)}
                        sx={{ '&:hover': { cursor: 'pointer' } }}
                      >
                        <TableCell>{formatDate2(movement.date)}</TableCell>
                        <TableCell>{getMovementTypeChip(movement.movement_type)}</TableCell>
                        <TableCell>{movement.document_number || '-'}</TableCell>
                        <TableCell>{movement.quantity}</TableCell>
                        <TableCell>{formatCurrency(movement.price || 0)}</TableCell>
                        <TableCell>
                          {movement.entity_name || '-'}
                        </TableCell>
                        <TableCell>{movement.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noTraceability')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.selectProductAndDateRange')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProductsTraceabilityReport;